import { addTo, pushTo } from '../../../util/index.js';
import { isAssociationManaged } from '../../case-vouchers/index.js';
import { FixedDistributionDateBehavior, } from '../../fixed-distributions/index.js';
import { balance } from './allocation.js';
import { createCostCeiling } from './ceiling.js';
import { applyCorrection } from './corrections.js';
import { dayIn } from './dates.js';
import { caseEvents, } from './events.js';
import { mirrorCorrection, pinOwnCorrection } from './joint.js';
import { accrue, createLedger } from './ledger.js';
const createInterestState = ({ voided, ...rule }) => ({
    ...rule,
    shadows: voided.map((period) => ({
        ...period,
        applied: false,
        rowIds: [],
        carries: [],
    })),
    chargedMonth: Number.NEGATIVE_INFINITY,
    carries: [],
    roots: [],
    capitalizations: [],
    derived: new Map(),
    unbooked: new Map(),
    dirty: false,
    rateDays: null,
    rows: [],
    visibleRowIds: [],
    lastMainRow: -1,
    chargedRowIds: new Map(),
    claimSteps: [],
    baseRemoved: null,
});
const createDebtLedger = (event, clock) => {
    const ledger = createLedger(clock.periods, clock.pinned);
    accrue(ledger, {
        from: event.day,
        to: event.day + 1,
        perDay: Math.abs(event.inputAmount),
        expiry: clock.expiry,
        clockFrom: clock.clockFrom,
        row: -1,
    });
    return ledger;
};
const createEntry = (caseState, event, voucher) => {
    const base = {
        ...event,
        caseState,
        voucher,
        appended: false,
        reservation: 0,
        dependents: [],
        distributed: 0,
        interruptsLimitation: false,
        ceilingCovered: false,
        unclaimed: { expired: 0, irrecoverable: 0 },
        compounds: false,
        compoundedIn: [],
    };
    if (event.kind === 'interest') {
        return {
            ...base,
            kind: event.kind,
            ledger: createLedger(caseState.policy.limitation.interest),
            interest: createInterestState(event.rule),
        };
    }
    if (event.kind === 'debt') {
        return {
            ...base,
            kind: event.kind,
            ledger: createDebtLedger(event, event.clock),
            interest: null,
        };
    }
    return { ...base, kind: event.kind, ledger: null, interest: null };
};
// The entries with a day, in the order of their days.
const scheduleBy = (entries, dayOf) => entries
    .flatMap((entry) => {
    const at = dayOf(entry);
    return at === null ? [] : [{ entry, at }];
})
    .toSorted((a, b) => a.at - b.at);
const prepareCase = (mode, until, input, caseIndex, creditCorrections) => {
    const { policy, options } = input;
    const included = input.caseVouchers.filter((voucher) => voucher.deletedAt == null &&
        !(mode === 'derive' && input.associated && isAssociationManaged(voucher)));
    const events = caseEvents(policy, until, options.monthStartBreakpoints === true, input.associated, included, input.documents);
    const caseState = {
        caseId: input.caseId,
        caseIndex,
        associated: input.associated,
        policy,
        options,
        until,
        entries: [],
        byId: new Map(),
        ledgerEntries: [],
        interestEntries: [],
        appendedByTier: policy.categoryTiers.map(() => []),
        lastAppended: null,
        breakpoints: events.breakpoints,
        nextBreakpoint: 0,
        lastAppendAt: null,
        stepFrom: 0,
        accruedTo: null,
        pastUntil: false,
        foundations: events.foundations,
        nextFoundation: 0,
        acknowledgedAt: Number.NEGATIVE_INFINITY,
        irrecoverable: [],
        nextIrrecoverable: 0,
        marked: [],
        nextMarked: 0,
        corrections: [],
        corrected: new Map(),
        correctionCaps: new Map(),
        correctionsFound: new Map(),
        closedAccruals: [],
        fixedInterestByRef: new Map(),
        ceiling: null,
        inEvent: false,
        eventClaims: undefined,
        manualBySource: new Map(),
        pinnedBySource: new Map(),
        earmarksBySource: new Map(),
        snapshots: new Map(),
        nextSnapshot: 0,
        yieldedAt: Date.now(),
    };
    caseState.entries = events.vouchers.map((event) => {
        const voucher = events.outputs[event.inputIndex];
        if (event.kind === 'interest') {
            voucher.amount = 0;
            delete voucher.originalAmount;
        }
        else {
            voucher.amount = event.inputAmount;
            voucher.originalAmount = event.inputAmount;
        }
        voucher.expiredAmount = 0;
        voucher.irrecoverableAmount = 0;
        voucher.nextExpiryDate = null;
        voucher.lastExpiryDate = null;
        return createEntry(caseState, event, voucher);
    });
    for (const entry of caseState.entries) {
        caseState.byId.set(entry.caseVoucherId, entry);
    }
    // Limitation is judged on vouchers in input order.
    caseState.ledgerEntries = caseState.entries
        .filter((entry) => entry.ledger !== null)
        .toSorted((a, b) => a.inputIndex - b.inputIndex);
    caseState.irrecoverable = scheduleBy(caseState.ledgerEntries, (entry) => entry.irrecoverableDay);
    caseState.marked = scheduleBy(caseState.ledgerEntries, (entry) => entry.markedDay);
    caseState.interestEntries = caseState.entries.filter((entry) => entry.kind === 'interest');
    // Roots and dependents in voucher order, whatever order the caller passed.
    for (const entry of caseState.entries) {
        const { refId } = entry;
        if (refId != null && entry.isFixedInterest && entry.kind === 'debt') {
            pushTo(caseState.fixedInterestByRef, refId, entry);
        }
        const ref = refId == null ? undefined : caseState.byId.get(refId);
        if (ref !== undefined && ref.kind === 'debt' && entry.ledger !== null) {
            ref.dependents.push(entry);
        }
    }
    for (const entry of caseState.interestEntries) {
        const interest = entry.interest;
        if (interest.compoundingType !== null && interest.refId !== null) {
            interest.roots = caseState.fixedInterestByRef.get(interest.refId) ?? [];
            for (const fixed of interest.roots) {
                fixed.compounds = true;
            }
        }
    }
    prepareDistributions(caseState, input.fixedDistributions, creditCorrections, mode);
    // What corrections applied, not what they asked for, leaves the ceiling.
    for (const { source, target, applied } of caseState.corrections) {
        addTo(caseState.corrected, source.caseVoucherId, applied);
        addTo(caseState.corrected, target.caseVoucherId, applied);
    }
    caseState.ceiling = createCostCeiling(included, caseState.corrected, options.costCeiling);
    for (const entry of caseState.interestEntries) {
        entry.ceilingCovered = caseState.ceiling?.covers(entry.voucher) ?? false;
    }
    return caseState;
};
const prepareDistributions = (caseState, fixedDistributions, creditCorrections, mode) => {
    const { associated, byId } = caseState;
    const corrections = [];
    for (const fd of fixedDistributions) {
        const source = byId.get(fd.fromCaseVoucherId);
        const target = byId.get(fd.toCaseVoucherId);
        // Derived distributions are replay output: read back, never derived from.
        if (source === undefined ||
            target === undefined ||
            (fd.derived && mode === 'derive')) {
            continue;
        }
        if (fd.dateBehavior === FixedDistributionDateBehavior.FromOriginal) {
            corrections.push(fd);
            continue;
        }
        // Mirrors pay only through their distributions, and derived ones are
        // replay output: read back, never derived from.
        const pinned = fd.derived || (associated && source.isMirror);
        if (pinned && mode === 'derive') {
            continue;
        }
        const bySource = pinned
            ? caseState.pinnedBySource
            : caseState.manualBySource;
        pushTo(bySource, fd.fromCaseVoucherId, fd);
        if (!pinned) {
            source.reservation += fd.amount;
            target.reservation -= fd.amount;
        }
    }
    caseState.corrections = corrections.map((fd) => {
        const source = byId.get(fd.fromCaseVoucherId);
        const target = byId.get(fd.toCaseVoucherId);
        return {
            id: fd.id,
            source,
            target,
            applied: applyCorrection(source, target, fd.amount, fd.id),
            byCreditNote: false,
        };
    });
    for (const [sourceId, amountsByTarget] of creditCorrections) {
        const source = byId.get(sourceId);
        if (source === undefined) {
            continue;
        }
        for (const [targetId, amount] of amountsByTarget) {
            const target = byId.get(targetId);
            caseState.corrections.push({
                id: `${sourceId}>${targetId}`,
                source,
                target,
                applied: applyCorrection(source, target, amount, undefined),
                byCreditNote: true,
            });
        }
    }
    // What reversals took from each payment, as applied.
    const reversed = new Map();
    for (const { source, target, applied } of caseState.corrections) {
        if (source.inputAmount < 0 && target.kind === 'credit') {
            addTo(reversed, target.caseVoucherId, applied);
        }
    }
    // An own payment interrupts limitation, unless reversed in full (D3).
    for (const entry of caseState.entries) {
        entry.interruptsLimitation =
            entry.interruptingPayment &&
                entry.inputAmount - (reversed.get(entry.caseVoucherId) ?? 0) > 0;
    }
};
// Reference earmarks, as v1: a positive voucher pointing at a debt is held for
// it from the start and paid when the positive voucher arrives.
const prepareEarmarks = (caseState) => {
    const { byId } = caseState;
    for (const source of caseState.entries) {
        const { refId } = source;
        if (source.inputAmount <= 0 || refId == null) {
            continue;
        }
        const target = byId.get(refId);
        if (target === undefined || target.inputAmount > 0) {
            continue;
        }
        const amount = Math.min(-balance(source), balance(target));
        if (amount <= 0) {
            continue;
        }
        source.reservation += amount;
        target.reservation -= amount;
        pushTo(caseState.earmarksBySource, source.caseVoucherId, {
            target,
            amount,
        });
    }
};
export const startReplay = (mode, inputs, untilISOString, creditCorrections) => {
    // The cases of a replay belong to one tenant.
    const until = inputs.length === 0 ? 0 : dayIn(inputs[0].policy.timezone)(untilISOString);
    const cases = inputs.map((input, index) => prepareCase(mode, until, input, index, creditCorrections));
    const counterparts = new Map();
    for (const caseState of cases) {
        // In voucher order.
        for (const entry of caseState.entries) {
            const { associationId } = entry;
            if (entry.ledger !== null && associationId != null && !entry.isMirror) {
                pushTo(counterparts, associationId, entry);
            }
        }
    }
    const run = {
        mode,
        cases,
        counterparts,
        jointAllocations: [],
        creditNotesPaid: new Map(),
        releaseCreditCaps: false,
    };
    if (mode === 'derive') {
        for (const caseState of cases) {
            for (const { source, target, applied, byCreditNote, } of caseState.corrections) {
                if (byCreditNote) {
                    pinOwnCorrection(run, source, target, applied);
                }
                mirrorCorrection(run, source, target, applied);
            }
        }
        // Read mode sees the mirrored corrections as corrections of the case.
        cases.forEach((caseState, index) => {
            caseState.ceiling = createCostCeiling(inputs[index].caseVouchers.filter((voucher) => caseState.byId.has(voucher.caseVoucherId)), caseState.corrected, inputs[index].options.costCeiling);
        });
    }
    cases.forEach(prepareEarmarks);
    return run;
};
//# sourceMappingURL=prepare.js.map